import { TREE_LOG_BLOCK_IDS } from '../game-data.js';
import { clamp } from '../utils/math-helpers.js';

const HOSTILE_MOB_TYPES = new Set(['zombie', 'skeleton', 'skeleton_archer', 'creeper', 'spider']);
const TREE_LOG_OBJECTIVE_BLOCK_IDS = Object.freeze([...TREE_LOG_BLOCK_IDS]);

function copyRewardList(reward) {
  if (!Array.isArray(reward)) return [];
  return reward
    .filter((entry) => entry && entry.itemId && Number(entry.count) > 0)
    .map((entry) => ({
      itemId: String(entry.itemId),
      count: Math.max(1, Math.floor(entry.count))
    }));
}

export function createObjectiveSystem({
  ui,
  dayNight,
  itemDefs,
  t
}) {
  const objectives = [
    {
      id: 'chop_logs',
      type: 'mine_block',
      blockIds: TREE_LOG_OBJECTIVE_BLOCK_IDS,
      target: 6,
      titleKey: 'objective.chop_logs.title',
      descKey: 'objective.chop_logs.desc',
      reward: copyRewardList([{ itemId: '6', count: 16 }])
    },
    {
      id: 'place_table',
      type: 'place_block',
      blockId: 11,
      target: 1,
      titleKey: 'objective.place_table.title',
      descKey: 'objective.place_table.desc',
      reward: copyRewardList([{ itemId: 'stick', count: 8 }])
    },
    {
      id: 'craft_stone_pickaxe',
      type: 'craft_item',
      itemId: 'stone_pickaxe',
      target: 1,
      titleKey: 'objective.craft_stone_pickaxe.title',
      descKey: 'objective.craft_stone_pickaxe.desc',
      reward: copyRewardList([{ itemId: 'torch', count: 8 }])
    },
    {
      id: 'mine_iron_ore',
      type: 'mine_block',
      blockId: 8,
      target: 4,
      titleKey: 'objective.mine_iron_ore.title',
      descKey: 'objective.mine_iron_ore.desc',
      reward: copyRewardList([{ itemId: 'coal', count: 6 }])
    },
    {
      id: 'craft_iron_pickaxe',
      type: 'craft_item',
      itemId: 'iron_pickaxe',
      target: 1,
      titleKey: 'objective.craft_iron_pickaxe.title',
      descKey: 'objective.craft_iron_pickaxe.desc',
      reward: copyRewardList([{ itemId: 'beef_raw', count: 4 }])
    },
    {
      id: 'reach_day_two',
      type: 'reach_day',
      target: 2,
      titleKey: 'objective.reach_day_two.title',
      descKey: 'objective.reach_day_two.desc',
      reward: copyRewardList([{ itemId: '5', count: 24 }])
    },
    {
      id: 'hunt_hostiles',
      type: 'kill_hostile',
      target: 4,
      titleKey: 'objective.hunt_hostiles.title',
      descKey: 'objective.hunt_hostiles.desc',
      reward: copyRewardList([{ itemId: 'iron_ingot', count: 4 }])
    },
    {
      id: 'collect_diamond',
      type: 'collect_item',
      itemId: 'diamond_gem',
      target: 2,
      titleKey: 'objective.collect_diamond.title',
      descKey: 'objective.collect_diamond.desc',
      reward: copyRewardList([{ itemId: 'diamond_pickaxe', count: 1 }])
    },
    {
      id: 'collect_obsidian',
      type: 'mine_block',
      blockId: 31,
      target: 4,
      titleKey: 'objective.collect_obsidian.title',
      descKey: 'objective.collect_obsidian.desc',
      reward: copyRewardList([{ itemId: 'flint', count: 2 }])
    }
  ];

  let activeIndex = 0;
  const progress = new Map();
  let dirty = true;

  const toastQueue = [];
  let toastTime = 0;
  let currentToast = null;
  let lastDayCount = dayNight.getDayCount();

  const hudEl = document.getElementById('objective-hud');
  const hudTitleEl = document.getElementById('objective-title');
  const hudStageEl = document.getElementById('objective-stage');
  const hudListEl = document.getElementById('objective-list');
  const toastEl = document.getElementById('objective-toast');

  function getItemDisplayName(itemId) {
    if (!itemId) return '';
    const key = `item.${itemId}`;
    const localized = t(key);
    if (localized !== key) return localized;
    return itemDefs[itemId]?.name || itemId;
  }

  function enqueueToast(message, kind = 'info') {
    if (!message) return;
    toastQueue.push({ message, kind });
  }

  function getActiveObjective() {
    if (activeIndex < 0 || activeIndex >= objectives.length) return null;
    return objectives[activeIndex];
  }

  function getStoredProgress(objectiveId) {
    return progress.get(objectiveId) || 0;
  }

  function getObjectiveProgress(obj) {
    if (!obj) return 0;
    if (obj.type === 'reach_day') {
      return clamp(dayNight.getDayCount(), 0, obj.target);
    }
    return clamp(getStoredProgress(obj.id), 0, obj.target);
  }

  function setObjectiveProgress(obj, nextValue) {
    if (!obj || obj.type === 'reach_day') return false;
    const prev = getStoredProgress(obj.id);
    const clamped = clamp(nextValue, 0, obj.target);
    if (clamped === prev) return false;
    progress.set(obj.id, clamped);
    dirty = true;
    return true;
  }

  function grantObjectiveReward(obj) {
    if (!obj || !obj.reward || obj.reward.length === 0) return;

    for (const rewardEntry of obj.reward) {
      ui.addItem(rewardEntry.itemId, rewardEntry.count);
      ui.assignItemToHotbar(rewardEntry.itemId);
      enqueueToast(
        t('objective.toast.reward', {
          item: getItemDisplayName(rewardEntry.itemId),
          count: rewardEntry.count
        }),
        'reward'
      );
    }
  }

  function handleObjectiveCompleted(obj) {
    enqueueToast(
      t('objective.toast.complete', {
        title: t(obj.titleKey)
      }),
      'complete'
    );

    grantObjectiveReward(obj);
    activeIndex += 1;
    dirty = true;

    const next = getActiveObjective();
    if (next) {
      enqueueToast(
        t('objective.toast.next', {
          title: t(next.titleKey)
        }),
        'next'
      );
    } else {
      enqueueToast(t('objective.toast.all_done'), 'complete');
    }
  }

  function updateCompletionState() {
    let guard = 0;
    while (guard < objectives.length) {
      guard += 1;
      const active = getActiveObjective();
      if (!active) break;

      const current = getObjectiveProgress(active);
      if (current < active.target) break;
      handleObjectiveCompleted(active);
    }
  }

  function addProgressToActiveObjective(delta) {
    const active = getActiveObjective();
    if (!active || delta <= 0 || active.type === 'reach_day') return;
    if (setObjectiveProgress(active, getStoredProgress(active.id) + delta)) {
      updateCompletionState();
    }
  }

  function getObjectiveDescription(obj) {
    if (!obj) return '';
    return t(obj.descKey, {
      target: obj.target,
      day: obj.target
    });
  }

  function createObjectiveCard(obj, opts = {}) {
    const { active = false } = opts;
    const card = document.createElement('div');
    card.className = `objective-card ${active ? 'active' : 'next'}`;

    const nameEl = document.createElement('div');
    nameEl.className = 'objective-name';
    nameEl.textContent = t(obj.titleKey);
    card.appendChild(nameEl);

    const descEl = document.createElement('div');
    descEl.className = 'objective-desc';
    descEl.textContent = getObjectiveDescription(obj);
    card.appendChild(descEl);

    if (!active) return card;

    const current = getObjectiveProgress(obj);
    const progressRatio = clamp(current / Math.max(1, obj.target), 0, 1);

    const progressWrap = document.createElement('div');
    progressWrap.className = 'objective-progress-wrap';

    const progressFill = document.createElement('div');
    progressFill.className = 'objective-progress-fill';
    progressFill.style.width = `${Math.round(progressRatio * 100)}%`;
    progressWrap.appendChild(progressFill);
    card.appendChild(progressWrap);

    const progressText = document.createElement('div');
    progressText.className = 'objective-progress-label';
    progressText.textContent = t('objective.progress', {
      value: Math.round(current),
      target: obj.target
    });
    card.appendChild(progressText);

    if (obj.reward && obj.reward.length > 0) {
      const rewardEl = document.createElement('div');
      rewardEl.className = 'objective-reward';
      const labels = obj.reward.map((entry) => `${getItemDisplayName(entry.itemId)} x${entry.count}`);
      rewardEl.textContent = `${t('objective.reward_label')}: ${labels.join(', ')}`;
      card.appendChild(rewardEl);
    }

    return card;
  }

  function renderHud() {
    if (!hudEl || !hudTitleEl || !hudListEl || !hudStageEl) return;
    if (!dirty) return;
    dirty = false;

    const total = objectives.length;
    const active = getActiveObjective();

    hudTitleEl.textContent = t('objective.header');
    hudStageEl.textContent = active
      ? t('objective.stage', { current: activeIndex + 1, total })
      : t('objective.stage_done');

    hudListEl.innerHTML = '';
    if (!active) {
      const doneEl = document.createElement('div');
      doneEl.className = 'objective-done';
      doneEl.textContent = t('objective.all_done');
      hudListEl.appendChild(doneEl);
      return;
    }

    hudListEl.appendChild(createObjectiveCard(active, { active: true }));

    const next = objectives[activeIndex + 1];
    if (next) {
      hudListEl.appendChild(createObjectiveCard(next, { active: false }));
    }
  }

  function renderToast() {
    if (!toastEl) return;

    if (!currentToast && toastQueue.length > 0) {
      currentToast = toastQueue.shift();
      toastTime = 3.4;
      toastEl.textContent = currentToast.message;
      toastEl.className = `objective-toast ${currentToast.kind || 'info'} show`;
      return;
    }

    if (!currentToast) return;
    if (toastTime > 0) return;

    toastEl.classList.remove('show');
    currentToast = null;
  }

  function onMineBlock(blockId, count = 1) {
    const active = getActiveObjective();
    if (!active || active.type !== 'mine_block') return;
    const minedBlockId = Number(blockId);
    if (Array.isArray(active.blockIds)) {
      if (!active.blockIds.includes(minedBlockId)) return;
    } else if (active.blockId !== minedBlockId) {
      return;
    }
    addProgressToActiveObjective(count);
  }

  function onPlaceBlock(blockId, count = 1) {
    const active = getActiveObjective();
    if (!active || active.type !== 'place_block') return;
    if (active.blockId !== Number(blockId)) return;
    addProgressToActiveObjective(count);
  }

  function onCraftItem(itemId, count = 1) {
    const active = getActiveObjective();
    if (!active || active.type !== 'craft_item') return;
    if (active.itemId !== itemId) return;
    addProgressToActiveObjective(count);
  }

  function onCollectItem(itemId, count = 1) {
    const active = getActiveObjective();
    if (!active || active.type !== 'collect_item') return;
    if (active.itemId !== itemId) return;
    addProgressToActiveObjective(count);
  }

  function onMobKilled(type, cause = 'unknown', count = 1) {
    const active = getActiveObjective();
    if (!active || active.type !== 'kill_hostile') return;
    if (cause !== 'player') return;
    if (!HOSTILE_MOB_TYPES.has(type)) return;
    addProgressToActiveObjective(count);
  }

  function refreshLocale() {
    dirty = true;
    renderHud();
  }

  function resetToastState() {
    toastQueue.length = 0;
    toastTime = 0;
    currentToast = null;
    if (toastEl) {
      toastEl.textContent = '';
      toastEl.className = 'objective-toast';
    }
  }

  function exportState() {
    const serializedProgress = {};
    for (const obj of objectives) {
      if (obj.type === 'reach_day') continue;
      const value = getStoredProgress(obj.id);
      if (value > 0) {
        serializedProgress[obj.id] = clamp(value, 0, obj.target);
      }
    }

    return {
      activeIndex,
      progress: serializedProgress
    };
  }

  function importState(snapshot = null) {
    progress.clear();
    activeIndex = 0;
    lastDayCount = dayNight.getDayCount();
    resetToastState();

    if (snapshot && typeof snapshot === 'object') {
      const nextActiveIndex = Math.floor(Number(snapshot.activeIndex));
      if (Number.isFinite(nextActiveIndex)) {
        activeIndex = clamp(nextActiveIndex, 0, objectives.length);
      }

      const savedProgress = snapshot.progress && typeof snapshot.progress === 'object'
        ? snapshot.progress
        : null;
      if (savedProgress) {
        for (const obj of objectives) {
          if (obj.type === 'reach_day') continue;
          const nextValue = Number(savedProgress[obj.id]);
          if (!Number.isFinite(nextValue) || nextValue <= 0) continue;
          progress.set(obj.id, clamp(nextValue, 0, obj.target));
        }
      }
    }

    dirty = true;
    renderHud();
    renderToast();
    return true;
  }

  function update(delta) {
    const active = getActiveObjective();
    if (active && active.type === 'reach_day') {
      const dayCount = dayNight.getDayCount();
      if (dayCount !== lastDayCount) {
        lastDayCount = dayCount;
        dirty = true;
      }
      updateCompletionState();
    } else {
      lastDayCount = dayNight.getDayCount();
    }

    toastTime = Math.max(0, toastTime - Math.max(0, delta));
    renderHud();
    renderToast();
  }

  // Initial render and first objective announcement.
  enqueueToast(
    t('objective.toast.next', {
      title: t(objectives[0].titleKey)
    }),
    'next'
  );
  updateCompletionState();
  renderHud();
  renderToast();

  return {
    update,
    refreshLocale,
    exportState,
    importState,
    onMineBlock,
    onPlaceBlock,
    onCraftItem,
    onCollectItem,
    onMobKilled
  };
}
